import { getToken } from "../api/authToken";
import { isSuperAdmin } from "./appRoles";

export interface DecodedToken {
  email: string;
  name: string;
  roles: string[];
  permissions: string[];
  groups: string[];
  tenantId: string;
  iat: number;
  exp: number;
}

/** True when JWT exp is in the past (optional clock-skew buffer in seconds). */
export function isJwtExpired(
  token: string | null | undefined,
  bufferSeconds = 30,
): boolean {
  const decoded = decodeJwtPayload(token);
  if (!decoded) return true;

  const exp = decoded.exp;
  if (typeof exp !== "number" || exp <= 0) {
    return false;
  }

  return Math.floor(Date.now() / 1000) >= exp - bufferSeconds;
}

/** Tenant token from storage only when not expired. */
export function getValidToken(): string | null {
  const token = getToken();
  if (!token || isJwtExpired(token)) {
    return null;
  }
  return token;
}

export function decodeJwtPayload(
  token: string | null | undefined,
): Record<string, unknown> | null {
  if (!token) return null;

  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;

    let payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    while (payload.length % 4) {
      payload += "=";
    }

    return JSON.parse(atob(payload)) as Record<string, unknown>;
  } catch (error) {
    console.error("Error decoding token:", error);
    return null;
  }
}

export function getEmailFromJwt(
  token: string | null | undefined,
): string | null {
  const decoded = decodeJwtPayload(token);
  if (!decoded) return null;

  for (const key of ["email", "preferred_username", "username"]) {
    const value = decoded[key];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }

  const sub = decoded.sub;
  if (typeof sub === "string" && sub.includes("@")) {
    return sub.trim();
  }

  return null;
}

function resolveJwtRoles(
  roles: string[],
  groups: string[],
): string[] {
  if (roles.length > 0) {
    return roles;
  }
  if (isSuperAdmin(groups)) {
    return ["sami_x_admin"];
  }
  return [];
}

function toDecodedToken(
  payload: Record<string, unknown>,
  token: string,
): DecodedToken {
  const email = getEmailFromJwt(token) ?? "";

  const groups = Array.isArray(payload.groups)
    ? payload.groups.filter((group): group is string => typeof group === "string")
    : [];
  const rolesFromClaim = Array.isArray(payload.roles)
    ? payload.roles.filter((role): role is string => typeof role === "string")
    : [];

  return {
    email,
    name: typeof payload.name === "string" ? payload.name : email,
    roles: resolveJwtRoles(rolesFromClaim, groups),
    permissions: Array.isArray(payload.permissions)
      ? payload.permissions.filter(
          (permission): permission is string => typeof permission === "string",
        )
      : [],
    groups,
    tenantId: readTenantIdFromJwtPayload(payload) ?? "",
    iat: typeof payload.iat === "number" ? payload.iat : 0,
    exp: typeof payload.exp === "number" ? payload.exp : 0,
  };
}

export const getDecodedToken = (): DecodedToken | null => {
  const token = getToken();
  const decoded = decodeJwtPayload(token);
  if (!token || !decoded) return null;
  return toDecodedToken(decoded, token);
};

function readTenantIdFromJwtPayload(
  payload: Record<string, unknown>,
): string | null {
  const candidate = payload.tenantId ?? payload.tenant_id ?? payload.tenant;
  if (typeof candidate === "string" && candidate.trim()) {
    return candidate.trim();
  }
  return null;
}

/** Tenant ID from JWT claims only (may be absent depending on token issuer). */
export const getTenantId = (): string | null => {
  const token = getToken();
  return getTenantIdFromToken(token);
};

export function getTenantIdFromToken(token: string | null | undefined): string | null {
  const decoded = decodeJwtPayload(token);
  if (!decoded) return null;
  return readTenantIdFromJwtPayload(decoded);
}

/**
 * Prefer tenant from /auth/me (AuthUser), then fall back to JWT claims.
 */
export const resolveTenantId = (
  authUserTenantId?: string | null,
): string | null => {
  const fromUser = authUserTenantId?.trim();
  if (fromUser) return fromUser;
  return getTenantId();
};